import { Injectable, OnDestroy } from '@angular/core';
import $ from 'jquery';

import { Page } from '../models/page';
import { PageFactory } from '../models/factories/page-factory';
import { RestService } from './rest.service';
import { environment } from '../../environments/environment';
import { ApplicationStateService } from './application-state.service';
import { Subscription } from 'rxjs';
import { CommunicationService } from './communication.service';
import { ChapterService } from './chapter.service';
import { ZoomEvent } from '../utils/events/zoom-event';
import { RouteService } from './route.service';
import { Location } from '@angular/common';
import _ from 'lodash';
import { sidebarConfig } from '../../environments/configs/sidebar-config';
import { PageChangedEvent } from '../utils/events/page-changed-event';
import { ArrayHelper } from '../utils/array';
import { WhiteboardEventsService } from '@lemberger/whiteboards';

@Injectable({
    providedIn: 'root'
})
export class PageService implements OnDestroy {

    isScrolling: boolean;

    viewablePagesRange: Array<number>;
    debouncedUpdateURLParameter = _.debounce(this.updateURLParameter, sidebarConfig.sidebarTransitionSpeed);
    private pages: Array<Page>;
    private idToPageMap: Map<number, Page> = new Map();
    private indexToIdMap: Map<number, Page> = new Map();
    private currentPages: Array<Page>;
    private visitedPagesHistory: Array<number>;
    private pageTurnSubscription: Subscription;
    private whiteboardEventsSubscription: Subscription;

    /**
     * Constructor
     * @param {RestService} restService
     * @param {RouteService} routeService
     * @param {ApplicationStateService} applicationStateService
     * @param {CommunicationService} communicationService
     * @param {ChapterService} chapterService
     * @param {WhiteboardEventsService} whiteboardEventService
     * @param {Location} location
     */
    constructor(private restService: RestService,
                private routeService: RouteService,
                private applicationStateService: ApplicationStateService,
                private communicationService: CommunicationService,
                private chapterService: ChapterService,
                private whiteboardEventService: WhiteboardEventsService,
                private location: Location) {

        this.pageTurnSubscription = this.communicationService.pageTurnAnnounced$.subscribe(pageChangedEvent => {
            if (pageChangedEvent.turnForward) {
                this.turnPageForward()
                    .then(result => {
                        this.communicationService.confirmPageTurn(result);
                    });
            } else if (pageChangedEvent.turnBackwards) {
                this.turnPageBackwards()
                    .then(result => {
                        this.communicationService.confirmPageTurn(result);
                    });
            } else {
                this.turnToPage(pageChangedEvent.moveToPage)
                    .then(result => {
                        this.communicationService.confirmPageTurn(result);
                    });
            }
        });

        this.whiteboardEventsSubscription = this.whiteboardEventService.pageTurns$.subscribe(pageId => {
            this.turnToPageById(pageId);
        });
    }

    ngOnDestroy(): void {
        this.pageTurnSubscription.unsubscribe();
        this.whiteboardEventsSubscription.unsubscribe();
    }

    /**
     * Initialize the pages of the current book.
     *
     * @returns {Promise<Page[]>}
     */
    init(): Promise<Array<Page>> {
        if (!this.applicationStateService.bookConfig.bookPermissions.page.view) {
            this.pages = [];
        }

        if (this.pages) {
            return new Promise(resolve => {
                resolve(this.pages);
            });
        }

        if (this.hasLimitedView()) {
            this.viewablePagesRange = [
                ...ArrayHelper.rangeLenght(0,
                    this.applicationStateService.bookConfig.firstPage + this.applicationStateService.bookConfig.viewablePages),
                this.applicationStateService.bookConfig.pageCount - 1
            ];
        }

        this.pages = [];
        this.idToPageMap = new Map();
        this.indexToIdMap = new Map();
        this.visitedPagesHistory = [0];

        const pageFactory: PageFactory = new PageFactory(this.applicationStateService);

        const self = this;

        return new Promise((resolve, reject) => {
            const route: string = self.routeService.getRelativeRoute(environment.endpointUrls.pages, {
                bookId: self.applicationStateService.bookId
            });

            return self.restService.get(route)
                .then(result => {
                    result.forEach(element => {
                        if (this.viewablePagesRange) {
                            if (this.viewablePagesRange.includes(element.index)) {
                                // Build each page
                                const page = pageFactory.buildFromObject(element);

                                self.idToPageMap.set(element.id, page);
                                self.indexToIdMap.set(page.index, page);
                                self.pages.push(page);
                            }
                            const oddNumberOfPages = (this.applicationStateService.bookConfig.firstPage +
                                this.applicationStateService.bookConfig.viewablePages) % 2;
                            const firstPageAfterViewable = this.applicationStateService.bookConfig.firstPage +
                                this.applicationStateService.bookConfig.viewablePages;

                            const lastPageIsNumbered = this.applicationStateService.bookConfig.pageCount - 1 ===
                                this.applicationStateService.bookConfig.lastPage;
                            const oddNumberOfPagesWithLastNumbered = (this.applicationStateService.bookConfig.firstPage +
                                this.applicationStateService.bookConfig.viewablePages - 1) % 2;

                            const shouldAddPage =
                                (oddNumberOfPages && firstPageAfterViewable === element.index && !lastPageIsNumbered) ||
                                (lastPageIsNumbered && oddNumberOfPagesWithLastNumbered && firstPageAfterViewable === element.index);

                            if (shouldAddPage) {
                                const page = pageFactory.createEmptyPage(element);
                                self.idToPageMap.set(page.id, page);
                                self.indexToIdMap.set(page.index, page);
                                self.pages.push(page);
                            }
                        } else {
                            // Build each page
                            const page = pageFactory.buildFromObject(element);

                            self.idToPageMap.set(element.id, page);
                            self.indexToIdMap.set(page.index, page);
                            self.pages.push(page);
                        }
                    });

                    self.setCurrentPages();
                    resolve(self.pages);
                });
        });
    }

    /**
     * Return all pages.
     *
     * @returns {Array<Page>}
     */
    getAllPages(): Array<Page> {
        return this.pages;
    }

    /**
     * Returns the current pages depending on the index in the ApplicationStateService.
     *
     * @returns {Page[]}
     */
    getCurrentPages(): Array<Page> {
        if (this.applicationStateService.isScrollMode()) {
            this.setCurrentPages();
        }

        return this.currentPages;
    }

    /**
     * Get all pages between start and end index.
     *
     * @param {number} startIndex
     * @param {number} endIndex
     *
     * @returns {Page[]}
     */
    getPages(startIndex: number, endIndex: number): Array<Page> {
        return this.pages.slice(Math.max(0, startIndex), Math.min(endIndex, this.pages.length));
    }

    /**
     * Get cover page.
     *
     * @returns {Page}
     */
    getCoverPage(): Page {
        return this.getPageByIndex(this.applicationStateService.bookConfig.cover);
    }

    /**
     * Get the page index of a specified id.
     *
     * @param {number} pageId
     * @returns {number}
     */
    getIndexByPageId(pageId: number): number {
        return this.getPageById(pageId)?.index;
    }

    /**
     * Get the page id of a specified index.
     *
     * @param {number} index
     * @returns {number}
     */
    getPageIdByIndex(index: number): number {
        return this.getPageByIndex(index).id;
    }

    /**
     * Get the page of a specified id.
     *
     * @param {number} pageId
     * @returns {Page}
     */
    getPageById(pageId: number): Page {
        return this.idToPageMap.get(pageId);
    }

    /**
     * Get the page of a specified index.
     *
     * @param {number} index
     * @returns {Page}
     */
    getPageByIndex(index: number): Page {
        return this.indexToIdMap.get(index);
    }

    /**
     * Converts viewable pages indexes to page IDs
     * @returns { Array<number> | null }
     */
    getGetViewablePagesIDs(): Array<number> | null {
        if (this.viewablePagesRange) {
            return this.viewablePagesRange.map(el => this.getPageIdByIndex(el));
        }

        return null;
    }

    /**
     * Gets index of page in context of the viewable pages array
     * @returns { Array<number> | null }
     */
    getGetViewablePagePossition(page: Page): number | null {
        if (this.hasLimitedView()) {
            return this.pages.indexOf(page);
        }

        return null;
    }

    /**
     * Converts the index from the index value of the page into
     * the page number which should be shown to the user.
     * @param {number} index
     * @returns {number}
     */
    getRealPageNumber(index: number): number {
        let realPageNumber = -1;
        const pageFirst = this.applicationStateService.bookConfig.firstPage;
        const pageLast = this.applicationStateService.bookConfig.lastPage;

        // Make real numbers and limit range
        if (index < pageFirst) {
            return realPageNumber;
        }

        // Calculate real page number
        realPageNumber = (index - pageFirst) + 1;

        return Math.min(realPageNumber, pageLast + 1);
    }

    /**
     * @param {number} pageId
     * @returns {number}
     */
    getRealPageNumberByPageID(pageId: number): number {
        return this.getRealPageNumber(this.getIndexByPageId(pageId));
    }

    getIndexFromPageNumber(pageNumber: number): number {

        const pageFirst = this.applicationStateService.bookConfig.firstPage;
        let index = pageNumber + pageFirst - 1;
        index = Math.max(0, Math.min(this.pages.length - 1, index));

        return index;
    }

    /**
     * Tells if the viewable pages setting should be take into account
     */
    hasLimitedView(): boolean {
        return this.applicationStateService.bookConfig.viewablePages &&
            (
                !this.applicationStateService.userData.bookAccessCode &&
                !this.applicationStateService.userData.digi4SchoolCode
            );
    }

    /**
     * Returns the real page numbers of the current pages.
     * @returns {Promise<number[]>}
     */
    getCurrentRealPageNumbers(): Promise<Array<number>> {

        return new Promise(resolve => {
            const numbers = this.currentPages.map(page => {
                return this.getRealPageNumber(page.index);
            });
            resolve(numbers);
        });
    }

    addToHistory(index: number): void {
        this.visitedPagesHistory.push(index);
    }

    removeFromHistory(): number {
        return this.visitedPagesHistory.pop();
    }

    /**
     * Check if it is possible to turn left.
     *
     * @returns {boolean}
     */
    isTurnLeftPossible(): boolean {
        const index = this.applicationStateService.currentIndex;

        return index > 0;
    }

    /**
     * Check if it is possible to turn right.
     *
     * @returns {boolean}
     */
    isTurnRightPossible(): boolean {
        const index = this.applicationStateService.currentIndex;

        return (index + this.applicationStateService.currentViewMode.pagesToShow < this.getPageCount());

    }

    isLeftPage(page: Page): boolean {
        const index = page.index;

        if (this.applicationStateService.currentViewMode.pagesToShow === 1 && index !== 0) {
            return true;
        }

        return index !== 0 && index % this.applicationStateService.currentViewMode.pagesToShow !== 0;
    }

    isRightPage(page: Page): boolean {
        const index = page.index;

        if (this.applicationStateService.currentViewMode.pagesToShow === 1) {
            return true;
        }

        return index === 0 ||
            index % this.applicationStateService.currentViewMode.pagesToShow !==
            this.applicationStateService.currentViewMode.pagesToShow - 1;
    }

    /**
     * Check if the page is in the center.
     *
     * @param {Page} page
     * @return {boolean}
     */
    isCenterPage(page: Page): boolean {
        if (this.applicationStateService.currentViewMode.pagesToShow === 1) {
            return true;
        }

        return page.index === 0 || page.index === this.pages.length - 1;
    }

    /**
     * Get count of pages.
     *
     * @returns {number}
     */
    getPageCount(): number {
        if (!this.pages) {
            return 0;
        }

        return this.pages.length;
    }

    getHighestIndex(): number {
        if (this.viewablePagesRange) {
            return this.viewablePagesRange[this.viewablePagesRange.length - 1];
        }

        if (this.pages) {
            return this.pages.length;
        }

        return -1;
    }

    getAmountOfCurrentPagesShown(): number {
        let pageCount;

        if (this.applicationStateService.currentIndex === 0 ||
            (this.applicationStateService.currentIndex
                >= this.getHighestIndex() - this.applicationStateService.currentViewMode.pagesToShow
                && this.getHighestIndex() % 2 === 0)) {
            pageCount = 1;
        } else {
            pageCount = this.applicationStateService.currentViewMode.pagesToShow;
        }

        return pageCount;
    }

    scrollToActivePage(): void {

        const thiz = this;
        thiz.isScrolling = true;
        setTimeout(() => {
            const element = $(`#image${this.applicationStateService.currentIndex}`)
                .first();
            if (element && element.offset()) {
                const container = $('#scrollablePagesContainer');
                container.stop();
                const offset = element.offset().top - container.offset().top + container.scrollTop();
                const scrollTop = (offset - 10 - container.height() / 2 + element.height() / 2) + 10;

                container.animate({
                    scrollTop
                }, 800, () => {
                    setTimeout(() => {
                        thiz.isScrolling = false;
                    }, 50);
                });
            } else {
                thiz.isScrolling = false;
            }
        }, sidebarConfig.sidebarTransitionSpeed);
    }

    /**
     * Adds the current page to the p-Parameter of the URL.
     */
    updateURLParameter(): void {

        const currentRealPageNumber = this.getRealPageNumber(this.applicationStateService.currentIndex);

        if (currentRealPageNumber > 0 && !this.applicationStateService.isOfflineModeEnabled) {

            const searchParams = new URLSearchParams(window.location.search);
            searchParams.set('p', currentRealPageNumber.toString());

            this.location.replaceState(this.location.path().split('?')[0], searchParams.toString());
        }
    }

    /**
     * Set the current shown pages.
     */
    private setCurrentPages(): void {
        const currentIndex: number = this.applicationStateService.currentIndex;

        let pagesToShow: number = this.applicationStateService.currentViewMode.pagesToShow;
        if (currentIndex === 0) {
            pagesToShow = 1;
        }

        this.currentPages = this.pages.slice(Math.max(currentIndex, 0), currentIndex + pagesToShow);
    }

    private turnPageForward(): Promise<Array<Page>> {

        const thiz = this;
        const turnPage = () => {
            const pageAmount = thiz.pages.length;
            const currentIndex = thiz.applicationStateService.currentIndex;
            const currentViewMode = thiz.applicationStateService.currentViewMode;
            const newIndex = currentIndex + currentViewMode.pagesToShow;

            if (currentIndex === 0) {
                return thiz.turnToPage(1);
            }

            return thiz.turnToPage(Math.min(newIndex, pageAmount));
        };

        return turnPage();
    }

    private turnPageBackwards(): Promise<Array<Page>> {

        const thiz = this;
        const turnPage = () => {
            const currentIndex = thiz.applicationStateService.currentIndex;
            const currentViewMode = thiz.applicationStateService.currentViewMode;
            const newIndex = currentIndex - currentViewMode.pagesToShow;

            return thiz.turnToPage(Math.max(0, newIndex));
        };

        return turnPage();
    }

    private turnToPage(index: number): Promise<Array<Page>> {

        const thiz = this;

        if (this.applicationStateService.isZoomed) {
            this.applicationStateService.zoomLevel = 1;
            this.communicationService.announceZoomLevelChanged(ZoomEvent.createZoomEvent(1));
        }

        const turnPage = () => {
            thiz.removeOldOverlays();

            if (index === 0) {
                thiz.applicationStateService.currentIndex = 0;
            } else {
                const pagesCount: number = thiz.pages.length;
                const pagesToShow: number = thiz.applicationStateService.currentViewMode.pagesToShow;

                let newIndex: number = Math.max(0, Math.min(index, pagesCount - 1));

                const numInPackage: number = (newIndex + 1) % pagesToShow;
                newIndex -= numInPackage;

                thiz.applicationStateService.currentIndex = Math.max(0, Math.min(newIndex, pagesCount));

                thiz.chapterService.getChapterFromIndex(thiz.applicationStateService.currentIndex)
                    .then(chapter => {
                        thiz.chapterService.turnToChapter(chapter);
                    });

            }

            thiz.setCurrentPages();
            this.debouncedUpdateURLParameter();

            return new Promise<Array<Page>>(resolve => resolve(thiz.getCurrentPages()));
        };

        return turnPage();
    }

    private turnToPageById(pageId: number): void {
        const pageIndex = this.getIndexByPageId(pageId);
        const pageChangeEvent = new PageChangedEvent();
        pageChangeEvent.moveToPage = pageIndex;
        this.communicationService.announcePageTurn(pageChangeEvent);
    }

    /*
    private cancelRequestFromOldPage(): void {

        //this.quickLinkService.cancelRequests();
        //this.pageTextService.cancelRequests();
        //this.readspeakerService.cancelRequests();
    }
    */

    private removeOldOverlays(): void {
        $('.modal-backdrop.fade.in')
            .remove();
    }
}
